import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  ButtonGroup,
  Flex,
  Icon,
  Stack,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiArrowRight, FiBriefcase, FiCheckCircle, FiExternalLink, FiXCircle } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import {
  NeedStatus,
  useCreateProcurementForNeedMutation,
  type NeedDto,
  type NeedPatchDto,
} from "../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../common/auth/useLoggedInWithOrgContextState";
import { useEditor } from "../../../common/editing/save-coordinator";
import { useApiError } from "../../../common/errors/useApiError";
import { useAppRole } from "../../../common/layout/v2/useAppRole";
import { procurementEntryHref } from "../sourcing-event/procurementEntryHref";

interface Props {
  needId: string;
}

export const NeedActions = ({ needId }: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { me } = useLoggedInWithOrgContextState();
  const { state, updateField, fieldStatus } = useEditor<NeedDto, NeedPatchDto>(needId);
  const apiError = useApiError();
  const role = useAppRole();
  const isAdvisor = role === "advisor";
  const isCreator = state.createdBy.id === me.id;

  const reject = useDisclosure();
  const withdraw = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [createProcurement, { isLoading: isCreatingProcurement }] = useCreateProcurementForNeedMutation();
  const isStatusSaving = fieldStatus("status") === "saving" || fieldStatus("status") === "pending";

  const onAccept = () => updateField({ status: NeedStatus.Approved });
  const onReject = () => {
    updateField({ status: NeedStatus.Rejected });
    reject.onClose();
  };
  const onWithdraw = () => {
    updateField({ status: NeedStatus.Withdrawn });
    withdraw.onClose();
  };
  const onCreateProcurement = async () => {
    try {
      const updated = await createProcurement({ needId }).unwrap();
      if (updated.procurement) navigate(procurementEntryHref(updated.procurement.status, updated.procurement.id));
    } catch (e) {
      apiError.trigger(e);
    }
  };

  const canAccept = state.status === NeedStatus.Reported && isAdvisor;
  const canReject = state.status === NeedStatus.Reported && isAdvisor;
  const canWithdraw = state.status === NeedStatus.Reported && isCreator;
  const canCreateProcurement = state.status === NeedStatus.Approved && isAdvisor;
  const hasAnyAction = canAccept || canReject || canWithdraw || canCreateProcurement;

  if (!hasAnyAction && !state.procurement) return null;

  return (
    <>
      <Stack
        spacing="3"
        bg="smBackground"
        border="1px solid"
        borderColor="smBorder"
        rounded="xl"
        p="4"
      >
        {state.procurement && (
          <Flex justify="space-between" align="center" gap="3" wrap="wrap">
            <Text fontSize="sm" color="smText">
              <Text as="span" color="smMuted">
                {t("Procurement created")}:
              </Text>{" "}
              <Text as="span" fontWeight="medium">
                {state.procurement.title?.trim() || t("Untitled")}
              </Text>
            </Text>
            <Button
              size="sm"
              variant="outline"
              colorScheme="teal"
              rightIcon={<Icon as={FiExternalLink} />}
              onClick={() => state.procurement && navigate(procurementEntryHref(state.procurement.status, state.procurement.id))}
            >
              {t("Open procurement")}
            </Button>
          </Flex>
        )}

        {hasAnyAction && (
          <ButtonGroup spacing="3" flexWrap="wrap" rowGap="2">
            {canAccept && (
              <Button
                colorScheme="teal"
                leftIcon={<Icon as={FiCheckCircle} />}
                onClick={onAccept}
                isLoading={isStatusSaving}
              >
                {t("Accept need")}
              </Button>
            )}
            {canReject && (
              <Button
                colorScheme="red"
                variant="outline"
                leftIcon={<Icon as={FiXCircle} />}
                onClick={reject.onOpen}
                isDisabled={isStatusSaving}
              >
                {t("Reject need")}
              </Button>
            )}
            {canWithdraw && (
              <Button
                colorScheme="red"
                variant="outline"
                leftIcon={<Icon as={FiArrowRight} />}
                onClick={withdraw.onOpen}
                isDisabled={isStatusSaving}
              >
                {t("Withdraw need")}
              </Button>
            )}
            {canCreateProcurement && (
              <Button
                colorScheme="teal"
                leftIcon={<Icon as={FiBriefcase} />}
                onClick={onCreateProcurement}
                isLoading={isCreatingProcurement}
              >
                {t("Create procurement")}
              </Button>
            )}
          </ButtonGroup>
        )}
      </Stack>

      <AlertDialog isOpen={reject.isOpen} leastDestructiveRef={cancelRef} onClose={reject.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{t("Reject this need?")}</AlertDialogHeader>
            <AlertDialogBody>{t("This is final and cannot be undone.")}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={reject.onClose} variant="ghost">
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" onClick={onReject} ml="3">
                {t("Reject")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      <AlertDialog isOpen={withdraw.isOpen} leastDestructiveRef={cancelRef} onClose={withdraw.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader>{t("Withdraw this need?")}</AlertDialogHeader>
            <AlertDialogBody>{t("This is final and cannot be undone.")}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={withdraw.onClose} variant="ghost">
                {t("Cancel")}
              </Button>
              <Button colorScheme="red" onClick={onWithdraw} ml="3">
                {t("Withdraw")}
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};
